import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import Loader from "_shared/_views/loading/loader";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import {
  BulkInvitationFieldOption,
  BulkInvitationRowValues,
  readText,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import {
  BulkInvitationAPIServiceError,
  TeamMemberRosterRow,
  useInviteTeamMembersMutation,
} from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";
import BulkInvitationGrid from "legal-entities/invitations/bulk/_views/bulk-invitation-grid";
import createTeamMemberRosterColumns from "legal-entities/invitations/bulk/team-member-roster-columns";
import { readApiError } from "_shared/_errors/api-error";
import OpenInvitationLinkSection from "legal-entities/invitations/member-invitations/_views/open-invitation-link-section";
import { useGetVendorRolloutBoardQuery } from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-api-service";
import { VENDOR_ROLLOUT_BOARD_MAX_PAGE_SIZE } from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-query";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import { useGetPermissionTemplatesQuery } from "permissions/_api/permission-templates-api-service";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

const PageContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: theme.spacing(1),
  overflow: "auto",
  padding: theme.spacing(2),
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));
const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(4, 0, 3),
}));
const ClientChoice = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  maxWidth: "32rem",
}));

/** What the select's "no particular client" option stores, since a `Guid` cannot be empty. */
const NO_ROLLOUT = "";
const CHOOSE_CURRENT_ROLLOUT = "__choose_current_rollout__";

function toRow(values: BulkInvitationRowValues): TeamMemberRosterRow {
  const accessTemplateId = readText(values, "accessTemplateId");
  return {
    email: readText(values, "email"),
    firstName: readText(values, "firstName"),
    lastName: readText(values, "lastName"),
    title: readText(values, "title"),
    requestedMarketplaceVisibility: values.requestedMarketplaceVisibility === true,
    accessTemplateId: accessTemplateId.length > 0 ? accessTemplateId : undefined,
  };
}

/**
 * A firm's team leader listing every colleague who will work and bill on a client's matters.
 *
 * Gated on administration access rather than on a gate of its own: entering thirty colleagues is
 * the same authority as entering one thirty times, and that is exactly what the API asserts.
 *
 * The client this list belongs to is asked for rather than passed in. A firm can be rolled out by
 * several clients at once and each roll-out names a different client in form letter 2, so the
 * answer is not derivable from the firm - it is the one thing only the person entering the list
 * knows. The choices come from the roll-out board scoped to this firm, which is the firm's own
 * entity-wide read of the same rows its clients see from the other side.
 */
export default function TeamMemberRosterPage() {
  const user = useSessionUser();
  // Present only when the page was reached from the prompt raised on the Attorney Hub proposal's
  // hire, whose topic is the firm. See the route in index.tsx.
  const namedEntityId = useParams().entityId;
  const [inviteTeamMembers] = useInviteTeamMembersMutation();
  const [chosenRolloutId, setChosenRolloutId] = useState<string | null>(null);
  const [unavailableRolloutId, setUnavailableRolloutId] = useState<string | null>(null);
  const [mustChooseCurrentRollout, setMustChooseCurrentRollout] = useState(false);

  // The organization's templates are answered only to a leader with Member Permissions (#6826), and
  // the API refuses a template named by anyone else; a leader without them gets no template column.
  const offersTemplates = user?.canManagePermissionTemplates() ?? false;
  const templatesQuery = useMemo(() => new PermissionTemplateQuery({}), []);
  const { data: templatesPage } = useGetPermissionTemplatesQuery({ query: templatesQuery }, { skip: !offersTemplates });

  // Skipped until there is a session, because the board decides scope from the id it is sent and
  // an absent one is the staff question - every client at once, which this caller cannot ask.
  // The board is paged (#5449) and this is a list to choose from, not a page to read, so it asks
  // for the largest page there is: one roll-out per client rolling this firm out, which no firm
  // comes near.
  const rolloutsQuery = useGetVendorRolloutBoardQuery(
    { vendorLegalEntityId: user?.legalEntityId, pageSize: VENDOR_ROLLOUT_BOARD_MAX_PAGE_SIZE },
    { skip: !user },
  );

  // Freshly allocated on every render and read by the `useMemo` below, so it is memoized
  // deliberately rather than defensively: a new array each render would rebuild the spec each
  // render, and the grid's columns with it. See app/CLAUDE.md, React Compiler.
  const templateOptions = useMemo<readonly BulkInvitationFieldOption[]>(
    () =>
      (templatesPage?.data ?? [])
        .filter((template) => Boolean(template.id))
        .map((template) => ({ value: String(template.id), label: template.name })),
    [templatesPage?.data],
  );
  const spec = useMemo(
    () => createTeamMemberRosterColumns(templateOptions, offersTemplates),
    [templateOptions, offersTemplates],
  );

  // Closed roll-outs are already off the board, so every row here is a client the firm is
  // currently being rolled out for.
  const rollouts = useMemo(
    () => (rolloutsQuery.data?.rows ?? []).map((row) => ({ id: row.rolloutId, clientName: row.clientName })),
    [rolloutsQuery.data?.rows],
  );
  const selectableRollouts = rollouts.filter((rollout) => rollout.id !== unavailableRolloutId);

  if (!user) return <Loader text="Loading..." />;
  if (!user.canInviteColleagues()) return <NotAuthorized />;

  const entityId = user.legalEntityId;
  // A prompt names the firm it is about. Following one that names a different firm - a forwarded
  // link, a leader who has since moved - must say so, rather than quietly opening the reader's own
  // roster and inviting people into an organization the link was not about.
  if (namedEntityId && namedEntityId.toLowerCase() !== entityId.value.toLowerCase()) {
    return <NotAuthorized />;
  }

  // One open roll-out is not a choice, it is a fact, so it is stated rather than asked. Asking
  // would invite the leader to get their only answer wrong, and a wrong one reaches a colleague
  // as another client's name.
  const onlyRollout = selectableRollouts.length === 1 ? selectableRollouts[0] : undefined;
  const selectedRolloutId = mustChooseCurrentRollout
    ? CHOOSE_CURRENT_ROLLOUT
    : onlyRollout?.id ?? chosenRolloutId ?? NO_ROLLOUT;
  const vendorRolloutId = selectedRolloutId.length > 0 && selectedRolloutId !== CHOOSE_CURRENT_ROLLOUT
    ? new Guid(selectedRolloutId)
    : undefined;
  const submitBlockedReason = mustChooseCurrentRollout
    ? "Choose a current client roll-out before sending the list."
    : undefined;

  return (
    <PageContainer>
      <PageTitle variant="h5">Invite your colleagues</PageTitle>

      {onlyRollout && !mustChooseCurrentRollout && (
        <Alert severity="info" sx={{ mb: 3, maxWidth: "32rem" }}>
          Everyone below is invited for <strong>{onlyRollout.clientName}</strong>, the client rolling
          your firm out. Their invitation names that client.
        </Alert>
      )}

      {(selectableRollouts.length > 1 || mustChooseCurrentRollout) && (
        <ClientChoice
          select
          label="Which client is this list for?"
          value={selectedRolloutId}
          onChange={(event) => {
            setChosenRolloutId(event.target.value);
            setMustChooseCurrentRollout(false);
          }}
          helperText={mustChooseCurrentRollout
            ? "Their invitation names this client. Choose a current client roll-out before sending."
            : "Their invitation names this client. Choose the last option for colleagues who are not joining for a particular client."}
        >
          {mustChooseCurrentRollout && (
            <MenuItem value={CHOOSE_CURRENT_ROLLOUT} disabled>
              Choose a current client roll-out
            </MenuItem>
          )}
          {selectableRollouts.map((rollout) => (
            <MenuItem key={rollout.id} value={rollout.id}>
              {rollout.clientName}
            </MenuItem>
          ))}
          {(!mustChooseCurrentRollout || selectableRollouts.length === 0) && (
            <MenuItem value={NO_ROLLOUT}>Not for a particular client</MenuItem>
          )}
        </ClientChoice>
      )}

      {/* A firm whose own board cannot be read still gets to enter its roster; what it loses is
          the client's name in the letter, not the colleagues' accounts. */}
      {rolloutsQuery.isError && (
        <Alert severity="info" sx={{ mb: 3, maxWidth: "32rem" }}>
          Attorney Hub could not look up which clients are rolling your firm out, so everyone below
          gets the standard invitation rather than one naming a client.
        </Alert>
      )}

      <BulkInvitationGrid
        spec={spec}
        submitLabel="Send invitations"
        submitBlockedReason={submitBlockedReason}
        description={
          "Everyone who will work and bill on your client's matters. Nobody can bill for work " +
          "until they have an account and a profile listing their credentials, so the list is " +
          "worth getting complete in one go."
        }
        onSubmit={async (rows) => {
          if (!vendorRolloutId) {
            return inviteTeamMembers({
              entityId,
              vendorRolloutId,
              rows: rows.map(toRow),
            }).unwrap();
          }

          try {
            return await inviteTeamMembers({
              entityId,
              vendorRolloutId,
              rows: rows.map(toRow),
            }).unwrap();
          } catch (error) {
            const rawError = error instanceof BulkInvitationAPIServiceError ? error.error : error;
            const response = readApiError(rawError);
            if (response.trusted && response.code === "vendor_rollout_unavailable") {
              setUnavailableRolloutId(vendorRolloutId.value);
              setChosenRolloutId(null);
              setMustChooseCurrentRollout(true);
              try {
                await rolloutsQuery.refetch();
              } catch {
                // Keep the roster blocked until the operator can choose a current client roll-out.
              }
            }
            throw error;
          }
        }}
      />

      {/* The Organization page's one invite button leads here, so the other way of inviting
          people has to be here too. */}
      <SectionDivider />
      <OpenInvitationLinkSection />
    </PageContainer>
  );
}
